import React from 'react';
import { TrendingUp, AlertTriangle, Activity, BarChart3 } from 'lucide-react';

const StatisticsPanel = ({ statistics, anomalies, actionDistribution }) => {
  const todayStats = statistics?.[0] || {
    total_actions: 0,
    unique_users: 0,
    creates: 0,
    updates: 0,
    deletes: 0,
    errors: 0,
    critical_events: 0
  };

  const totalActions = statistics?.reduce((sum, stat) => sum + (stat?.total_actions || 0), 0) || 0;
  const totalErrors = statistics?.reduce((sum, stat) => sum + (stat?.errors || 0), 0) || 0;

  return (
    <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
      {/* Daily Activity */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Atividade Hoje
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div>
              <p className="text-xs text-blue-600 font-medium">Total de Ações</p>
              <p className="text-2xl font-bold text-blue-700">{todayStats?.total_actions}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div>
              <p className="text-xs text-green-600 font-medium">Usuários Ativos</p>
              <p className="text-2xl font-bold text-green-700">{todayStats?.unique_users}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">👥</span>
            </div>
          </div>
        </div>
      </div>
      {/* Action Distribution */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Distribuição de Ações
        </h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Criações</span>
            <span className="font-semibold text-green-600">{todayStats?.creates}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full"
              style={{ width: `${(todayStats?.creates / (todayStats?.total_actions || 1)) * 100}%` }}
            ></div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Atualizações</span>
            <span className="font-semibold text-blue-600">{todayStats?.updates}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full"
              style={{ width: `${(todayStats?.updates / (todayStats?.total_actions || 1)) * 100}%` }}
            ></div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Exclusões</span>
            <span className="font-semibold text-red-600">{todayStats?.deletes}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-red-500 h-2 rounded-full"
              style={{ width: `${(todayStats?.deletes / (todayStats?.total_actions || 1)) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
      {/* System Health */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Saúde do Sistema
        </h3>
        <div className="space-y-3">
          <div className="p-3 bg-green-50 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-green-600 font-medium">Operações (30d)</span>
              <span className="text-sm font-bold text-green-700">{totalActions}</span>
            </div>
            <div className="w-full bg-green-200 rounded-full h-1.5">
              <div className="bg-green-500 h-1.5 rounded-full" style={{ width: '85%' }}></div>
            </div>
          </div>

          <div className="p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-yellow-600 font-medium">Erros Totais</span>
              <span className="text-sm font-bold text-yellow-700">{totalErrors}</span>
            </div>
            <div className="w-full bg-yellow-200 rounded-full h-1.5">
              <div className="bg-yellow-500 h-1.5 rounded-full" style={{ width: '15%' }}></div>
            </div>
          </div>

          <div className="p-3 bg-red-50 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-red-600 font-medium">Críticos Hoje</span>
              <span className="text-sm font-bold text-red-700">{todayStats?.critical_events}</span>
            </div>
            <div className="w-full bg-red-200 rounded-full h-1.5">
              <div className="bg-red-500 h-1.5 rounded-full" style={{ width: '5%' }}></div>
            </div>
          </div>
        </div>
      </div>
      {/* Anomaly Alerts */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Alertas de Anomalias
        </h3>
        {anomalies?.length === 0 ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-2xl">✓</span>
            </div>
            <p className="text-sm text-gray-600">Nenhuma anomalia detectada</p>
          </div>
        ) : (
          <div className="space-y-2">
            {anomalies?.slice(0, 5)?.map((anomaly, index) => (
              <div key={index} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-orange-800">
                      {anomaly?.user_email}
                    </p>
                    <p className="text-xs text-orange-600 mt-1">
                      {anomaly?.action_type} - {anomaly?.count} vezes
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Hora: {anomaly?.hour}h
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Action Distribution Chart */}
      {actionDistribution?.length > 0 && (
        <div className="p-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Tipos de Ação (30d)</h3>
          <div className="space-y-2">
            {actionDistribution?.map(item => (
              <div key={item?.action} className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{item?.action}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ 
                        width: `${(item?.count / Math.max(...actionDistribution?.map(a => a?.count))) * 100}%` 
                      }}
                    ></div>
                  </div>
                  <span className="font-semibold text-gray-700 w-8 text-right">{item?.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsPanel;